import React, { useState, useRef, useEffect } from 'react';
import { styled } from 'styletron-react';

const PRESET_COLORS = [
  '#000000', '#FFFFFF', '#111827', '#374151', '#6B7280', '#9CA3AF', '#D1D5DB',
  '#005488', '#276EF1', '#3B82F6', '#60A5FA', '#93C5FD', '#DBEAFE',
  '#059669', '#10B981', '#34D399',
  '#D44333', '#EF4444', '#F87171',
  '#F59E0B', '#FBBF24', '#FDE68A',
  '#F6E4CC', '#F3F4F6', '#E5E7EB',
];

export interface ColorPreset {
  hex: string;
  name?: string;
  /** Shown as tooltip; falls back to `name`. */
  hint?: string;
  /** Visible but not selectable (e.g. a theme the app can't render yet). */
  disabled?: boolean;
}

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  /** Replaces the generic swatch grid with named presets (e.g. ad themes). */
  presets?: ColorPreset[];
}

const DEFAULT_PRESETS: ColorPreset[] = PRESET_COLORS.map((hex) => ({ hex }));

export const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange, label, presets }) => {
  const [showPopover, setShowPopover] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const swatches = presets ?? DEFAULT_PRESETS;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShowPopover(false);
      }
    };
    if (showPopover) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPopover]);

  return (
    <Container ref={ref}>
      {label && <Label>{label}</Label>}
      <InputRow>
        <Swatch
          $color={value}
          onClick={() => setShowPopover(!showPopover)}
        />
        <HexInput
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          maxLength={7}
        />
      </InputRow>
      {showPopover && (
        <Popover>
          <PresetGrid>
            {swatches.map((p) => (
              <PresetSwatch
                key={p.hex}
                $color={p.hex}
                $selected={value.toLowerCase() === p.hex.toLowerCase()}
                $disabled={!!p.disabled}
                title={p.hint ?? p.name ?? p.hex}
                aria-label={p.name ?? p.hex}
                aria-disabled={p.disabled || undefined}
                onClick={() => {
                  if (p.disabled) return;
                  onChange(p.hex);
                  setShowPopover(false);
                }}
              />
            ))}
          </PresetGrid>
          <Divider />
          <CustomRow>
            <CustomLabel>Custom</CustomLabel>
            <NativeInput
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          </CustomRow>
        </Popover>
      )}
    </Container>
  );
};

// Styled components

const Container = styled('div', {
  position: 'relative' as const,
});

const Label = styled('div', {
  fontSize: '13px',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '6px',
});

const InputRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

const Swatch = styled<'div', { $color: string }>('div', ({ $color }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '8px',
  backgroundColor: $color || '#000',
  border: '2px solid #E5E7EB',
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'transform 0.1s ease',
  ':hover': { transform: 'scale(1.05)' },
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
}));

const HexInput = styled('input', {
  padding: '8px 12px',
  fontSize: '14px',
  fontFamily: 'monospace',
  border: '1px solid #D1D5DB',
  borderRadius: '8px',
  width: '100px',
  outline: 'none',
  ':focus': { borderColor: '#111827', boxShadow: '0 0 0 2px rgba(17,24,39,0.1)' },
});

const Popover = styled('div', {
  position: 'absolute' as const,
  top: 'calc(100% + 8px)',
  left: '0',
  zIndex: 10001,
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
  padding: '16px',
  width: '260px',
});

const PresetGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '6px',
});

const PresetSwatch = styled<'div', { $color: string; $selected: boolean; $disabled?: boolean }>(
  'div',
  ({ $color, $selected, $disabled }) => ({
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    backgroundColor: $color,
    cursor: $disabled ? 'not-allowed' : 'pointer',
    opacity: $disabled ? 0.35 : 1,
    border: $selected ? '2px solid var(--tenant-primary, #111827)' : '1px solid #E5E7EB',
    boxShadow: $selected ? '0 0 0 2px color-mix(in srgb, var(--tenant-primary, #111827) 20%, transparent)' : 'none',
    transition: 'all 0.1s ease',
    ':hover': $disabled ? {} : { transform: 'scale(1.15)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
  }),
);

const Divider = styled('div', {
  height: '1px',
  backgroundColor: '#E5E7EB',
  margin: '12px 0',
});

const CustomRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const CustomLabel = styled('span', {
  fontSize: '13px',
  color: '#6B7280',
});

const NativeInput = styled('input', {
  width: '36px',
  height: '36px',
  padding: '0',
  border: '2px solid #E5E7EB',
  borderRadius: '8px',
  cursor: 'pointer',
  backgroundColor: 'transparent',
  '::-webkit-color-swatch-wrapper': { padding: '2px' },
  '::-webkit-color-swatch': { borderRadius: '4px', border: 'none' },
});
